import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { BRAND, SERVICES } from "../lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "",
  address: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) er.email = "Enter a valid email";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) er.phone = "Enter a valid phone";
    if (!form.service) er.service = "Choose a service";
    if (!form.message.trim()) er.message = "Please share a brief message";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      setForm(initial);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please call us at " + BRAND.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section
        data-testid="contact-hero"
        className="relative py-24 bg-[#0F1A14] text-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-6" />
          <p className="uppercase tracking-[0.2em] text-xs text-[#86EFAC] mb-3 font-body">Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
            Let's talk about your yard.
          </h1>
          <p className="mt-5 text-gray-300 max-w-2xl font-body text-lg">
            Send us a quick message or give us a call — we'll get back to you
            with a free quote, fast.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-5">
          {/* INFO */}
          <aside className="lg:col-span-2 space-y-6" data-testid="contact-info">
            <div className="bg-gradient-to-br from-[#15803D] to-[#166534] rounded-3xl p-8 text-white">
              <h2 className="font-display text-2xl font-bold">Get in touch</h2>
              <p className="mt-2 text-white/90 font-body text-sm">
                We respond to most inquiries within one business day.
              </p>
              <div className="mt-7 space-y-5">
                <a
                  href={`tel:${BRAND.phoneTel}`}
                  data-testid="contact-info-phone"
                  className="flex items-start gap-4 group"
                >
                  <div className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center group-hover:bg-white group-hover:text-[#15803D] transition-colors">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/70">Phone</div>
                    <div className="font-display text-lg">{BRAND.phone}</div>
                  </div>
                </a>
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid="contact-info-email"
                  className="flex items-start gap-4 group"
                >
                  <div className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center group-hover:bg-white group-hover:text-[#15803D] transition-colors">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/70">Email</div>
                    <div className="font-body text-base break-all">{BRAND.email}</div>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/70">Address</div>
                    <div className="font-body text-sm leading-relaxed">
                      {BRAND.address}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-white/15 grid place-items-center">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-white/70">Hours</div>
                    <div className="font-body text-sm">Mon – Sat · 7:00 AM – 6:00 PM</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
              <h3 className="font-display font-semibold text-gray-900">Founder-Led Service</h3>
              <p className="mt-2 text-sm text-gray-600 font-body leading-relaxed">
                When you call, you're talking to founder {BRAND.owner} or one
                of our lead crew. No call centers — just real people who care
                about your yard.
              </p>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-10 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.15)]">
              {submitted ? (
                <div data-testid="contact-success" className="text-center py-12 px-4">
                  <div className="mx-auto h-16 w-16 rounded-full bg-[#15803D]/10 grid place-items-center text-[#15803D]">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold text-gray-900">
                    Thank you!
                  </h3>
                  <p className="mt-3 text-gray-600 font-body max-w-md mx-auto">
                    Your inquiry has been sent. We'll get back to you shortly
                    with a free quote. For urgent requests, give us a call at{" "}
                    <a href={`tel:${BRAND.phoneTel}`} className="text-[#15803D] font-semibold">
                      {BRAND.phone}
                    </a>
                    .
                  </p>
                  <Button
                    data-testid="contact-send-another"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 bg-[#15803D] hover:bg-[#166534] text-white rounded-full px-6"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  noValidate
                >
                  <div>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
                      Request a free quote
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 font-body">
                      Fill out the form and we'll be in touch within one
                      business day.
                    </p>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="name" className="font-body">Full Name *</Label>
                      <Input
                        id="name"
                        data-testid="input-name"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Jane Doe"
                        className="mt-1.5 h-11"
                      />
                      {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-body">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="input-email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@example.com"
                        className="mt-1.5 h-11"
                      />
                      {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="phone" className="font-body">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="input-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="(561) 555-0123"
                        className="mt-1.5 h-11"
                      />
                      {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <Label htmlFor="service" className="font-body">Service Needed *</Label>
                      <Select value={form.service} onValueChange={update("service")}>
                        <SelectTrigger
                          id="service"
                          data-testid="select-service"
                          className="mt-1.5 h-11"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES.map((s) => (
                            <SelectItem
                              key={s.slug}
                              value={s.title}
                              data-testid={`select-option-${s.slug}`}
                            >
                              {s.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="Other / Not Sure" data-testid="select-option-other">
                            Other / Not Sure
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.service && <p className="text-xs text-red-600 mt-1">{errors.service}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="font-body">Address (optional)</Label>
                    <Input
                      id="address"
                      data-testid="input-address"
                      value={form.address}
                      onChange={update("address")}
                      placeholder="123 Main St, Jupiter, FL"
                      className="mt-1.5 h-11"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-body">Message *</Label>
                    <Textarea
                      id="message"
                      data-testid="input-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us a bit about your property and what you're looking for..."
                      rows={5}
                      className="mt-1.5"
                    />
                    {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="w-full sm:w-auto bg-[#DC2626] hover:bg-red-700 text-white rounded-full px-8 h-12 text-base"
                  >
                    {submitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" /> Send Inquiry
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 font-body">
                    By submitting this form, you agree to be contacted by EL
                    Lawn Services Inc regarding your inquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
